/**
 * Tree Operations Utility Functions
 * 
 * Pure functions for tree hierarchy manipulation, validation, and traversal.
 * These functions have no side effects and can be tested independently.
 */

import { TreeGridRow, TreeOperationResult } from '../types';

/**
 * Flatten tree structure into visible rows based on expansion state
 */
export function flattenTree<T = any>(
  rows: TreeGridRow<T>[],
  expandedIds: Set<string>
): TreeGridRow<T>[] {
  const result: TreeGridRow<T>[] = [];
  const rowsById = new Map(rows.map(row => [row.id, row]));
  
  // Find root rows (no parent)
  const rootRows = rows.filter(row => row.parentId === null);
  
  // Sort root rows by sortOrder
  rootRows.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  
  function traverse(row: TreeGridRow<T>, depth: number) {
    result.push({ ...row, depth });
    
    if (row.isExpanded && expandedIds.has(row.id)) {
      const children = row.childrenIds
        .map(id => rowsById.get(id))
        .filter((child): child is TreeGridRow<T> => child !== undefined);
      
      // Sort children by sortOrder
      children.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      
      children.forEach(child => traverse(child, depth + 1));
    }
  }
  
  rootRows.forEach(row => traverse(row, 0));
  
  return result;
}

/**
 * Build tree structure from flat array based on parent relationships
 */
export function buildTree<T = any>(rows: TreeGridRow<T>[]): TreeGridRow<T>[] {
  const rowsById = new Map<string, TreeGridRow<T>>();
  const rootRows: TreeGridRow<T>[] = [];
  
  // First pass: index all rows
  rows.forEach(row => {
    rowsById.set(row.id, { ...row, childrenIds: [] });
  });
  
  // Second pass: build parent-child relationships
  rows.forEach(row => {
    const currentRow = rowsById.get(row.id)!;
    
    if (row.parentId === null) {
      rootRows.push(currentRow);
    } else {
      const parent = rowsById.get(row.parentId);
      if (parent) {
        if (!parent.childrenIds.includes(row.id)) {
          parent.childrenIds.push(row.id);
        }
      } else {
        // Parent doesn't exist, make this a root row
        currentRow.parentId = null;
        rootRows.push(currentRow);
      }
    }
  });
  
  return Array.from(rowsById.values());
}

/**
 * Check if moving a row would create a cycle
 */
export function wouldCreateCycle(
  rowId: string,
  newParentId: string | null,
  rowsById: Map<string, TreeGridRow>
): boolean {
  if (newParentId === null) return false;
  if (rowId === newParentId) return true;
  
  let currentId: string | null = newParentId;
  const visited = new Set<string>();
  
  while (currentId !== null) {
    if (currentId === rowId) return true;
    if (visited.has(currentId)) return true; // Existing cycle
    
    visited.add(currentId);
    const row = rowsById.get(currentId);
    currentId = row?.parentId || null;
  }
  
  return false;
}

/**
 * Get all descendant IDs of a row
 */
export function getDescendantIds(
  rowId: string,
  rowsById: Map<string, TreeGridRow>
): string[] {
  const result: string[] = [];
  const row = rowsById.get(rowId);
  
  if (!row) return result;
  
  function traverse(id: string) {
    const currentRow = rowsById.get(id);
    if (!currentRow) return;
    
    currentRow.childrenIds.forEach(childId => {
      result.push(childId);
      traverse(childId);
    });
  }
  
  traverse(rowId);
  return result;
}

/**
 * Get all ancestor IDs of a row
 */
export function getAncestorIds(
  rowId: string,
  rowsById: Map<string, TreeGridRow>
): string[] {
  const result: string[] = [];
  let currentId: string | null = rowId;
  
  while (currentId !== null) {
    const row = rowsById.get(currentId);
    if (!row) break;
    
    if (row.parentId !== null) {
      result.push(row.parentId);
    }
    currentId = row.parentId;
  }
  
  return result;
}

/**
 * Move a row to a new parent at a specific index
 */
export function moveRow<T = any>(
  rowId: string,
  newParentId: string | null,
  targetIndex: number,
  rows: TreeGridRow<T>[]
): TreeOperationResult {
  const rowsById = new Map(rows.map(row => [row.id, row]));
  const row = rowsById.get(rowId);
  
  if (!row) {
    return { success: false, error: 'Row not found' };
  }
  
  // Check for cycles
  if (wouldCreateCycle(rowId, newParentId, rowsById)) {
    return { success: false, error: 'Cannot move row: would create a cycle' };
  }
  
  // Check if target is a descendant
  const descendants = getDescendantIds(rowId, rowsById);
  if (newParentId && descendants.includes(newParentId)) {
    return { success: false, error: 'Cannot move row under its own descendant' };
  }
  
  // Remove from old parent
  if (row.parentId !== null) {
    const oldParent = rowsById.get(row.parentId);
    if (oldParent) {
      oldParent.childrenIds = oldParent.childrenIds.filter(id => id !== rowId);
    }
  }
  
  // Update row's parent
  row.parentId = newParentId;
  
  // Add to new parent
  if (newParentId !== null) {
    const newParent = rowsById.get(newParentId);
    if (newParent) {
      // Insert at specific index
      const insertIndex = Math.min(targetIndex, newParent.childrenIds.length);
      newParent.childrenIds.splice(insertIndex, 0, rowId);
    }
  }
  
  // Update sort orders
  const siblings = newParentId
    ? (rowsById.get(newParentId)?.childrenIds || [])
    : rows.filter(r => r.parentId === null).map(r => r.id);
  
  siblings.forEach((siblingId, index) => {
    const sibling = rowsById.get(siblingId);
    if (sibling) {
      sibling.sortOrder = index;
    }
  });
  
  return {
    success: true,
    affectedRows: [rowId, row.parentId, newParentId].filter(Boolean) as string[]
  };
}

/**
 * Indent a row (move it under its previous sibling)
 */
export function indentRow<T = any>(
  rowId: string,
  rows: TreeGridRow<T>[],
  maxDepth?: number
): TreeOperationResult {
  const rowsById = new Map(rows.map(row => [row.id, row]));
  const row = rowsById.get(rowId);
  
  if (!row) {
    return { success: false, error: 'Row not found' };
  }
  
  // Check max depth
  if (maxDepth !== undefined && row.depth >= maxDepth) {
    return { success: false, error: `Maximum depth of ${maxDepth} reached` };
  }
  
  // Get siblings
  const siblings = rows.filter(r => r.parentId === row.parentId);
  siblings.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  
  const currentIndex = siblings.findIndex(r => r.id === rowId);
  if (currentIndex <= 0) {
    return { success: false, error: 'Cannot indent: no previous sibling' };
  }
  
  const newParent = siblings[currentIndex - 1];
  
  return moveRow(rowId, newParent.id, newParent.childrenIds.length, rows);
}

/**
 * Outdent a row (move it to the same level as its parent)
 */
export function outdentRow<T = any>(
  rowId: string,
  rows: TreeGridRow<T>[]
): TreeOperationResult {
  const rowsById = new Map(rows.map(row => [row.id, row]));
  const row = rowsById.get(rowId);
  
  if (!row) {
    return { success: false, error: 'Row not found' };
  }
  
  if (row.parentId === null) {
    return { success: false, error: 'Cannot outdent: already at root level' };
  }
  
  const parent = rowsById.get(row.parentId);
  if (!parent) {
    return { success: false, error: 'Parent not found' };
  }
  
  const grandparentId = parent.parentId;
  const grandparent = grandparentId ? rowsById.get(grandparentId) : null;
  
  // Find parent's index among its siblings
  const parentSiblings = rows.filter(r => r.parentId === grandparentId);
  parentSiblings.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const parentIndex = parentSiblings.findIndex(r => r.id === parent.id);
  
  // Insert after parent
  return moveRow(rowId, grandparentId, parentIndex + 1, rows);
}

/**
 * Toggle row expansion
 */
export function toggleExpansion(
  rowId: string,
  expandedIds: Set<string>
): Set<string> {
  const newSet = new Set(expandedIds);
  if (newSet.has(rowId)) {
    newSet.delete(rowId);
  } else {
    newSet.add(rowId);
  }
  return newSet;
}

/**
 * Expand all ancestors of a row
 */
export function expandAncestors(
  rowId: string,
  rowsById: Map<string, TreeGridRow>,
  expandedIds: Set<string>
): Set<string> {
  const ancestors = getAncestorIds(rowId, rowsById);
  const newSet = new Set(expandedIds);
  ancestors.forEach(id => newSet.add(id));
  return newSet;
}

/**
 * Calculate depth of a row
 */
export function calculateDepth(
  rowId: string,
  rowsById: Map<string, TreeGridRow>
): number {
  let depth = 0;
  let currentId: string | null = rowId;
  
  while (currentId !== null) {
    const row = rowsById.get(currentId);
    if (!row) break;
    
    if (row.parentId !== null) {
      depth++;
    }
    currentId = row.parentId;
  }
  
  return depth;
}

/**
 * Get row path (list of ancestor IDs from root to row)
 */
export function getRowPath(
  rowId: string,
  rowsById: Map<string, TreeGridRow>
): string[] {
  const path: string[] = [rowId];
  let currentId: string | null = rowId;
  
  while (currentId !== null) {
    const row = rowsById.get(currentId);
    if (!row) break;
    
    if (row.parentId !== null) {
      path.unshift(row.parentId);
    }
    currentId = row.parentId;
  }
  
  return path;
}

/**
 * Find next sibling row
 */
export function getNextSibling(
  rowId: string,
  rows: TreeGridRow[]
): TreeGridRow | null {
  const row = rows.find(r => r.id === rowId);
  if (!row) return null;
  
  const siblings = rows.filter(r => r.parentId === row.parentId);
  siblings.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  
  const currentIndex = siblings.findIndex(r => r.id === rowId);
  return siblings[currentIndex + 1] || null;
}

/**
 * Find previous sibling row
 */
export function getPreviousSibling(
  rowId: string,
  rows: TreeGridRow[]
): TreeGridRow | null {
  const row = rows.find(r => r.id === rowId);
  if (!row) return null;
  
  const siblings = rows.filter(r => r.parentId === row.parentId);
  siblings.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  
  const currentIndex = siblings.findIndex(r => r.id === rowId);
  return siblings[currentIndex - 1] || null;
}

/**
 * Check if a row has children
 */
export function hasChildren(row: TreeGridRow): boolean {
  return row.childrenIds.length > 0;
}

/**
 * Check if a row is a leaf (no children)
 */
export function isLeaf(row: TreeGridRow): boolean {
  return row.childrenIds.length === 0;
}

/**
 * Get total count of rows in tree (including collapsed)
 */
export function getTotalRowCount(rows: TreeGridRow[]): number {
  return rows.length;
}

/**
 * Get visible row count based on expansion state
 */
export function getVisibleRowCount(
  rows: TreeGridRow[],
  expandedIds: Set<string>
): number {
  return flattenTree(rows, expandedIds).length;
}
